import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import api from '../../lib/axios'
import SpeczeLogo from '../../assets/SpeczeLogo'

// ── Helpers ────────────────────────────────────────────────────────────────────

const TYPE_META = {
  Document:      { icon: '📄', color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/20' },
  Specification: { icon: '◈',  color: 'text-pink-400',   bg: 'bg-pink-500/10',   border: 'border-pink-500/20' },
  TestPlan:      { icon: '🧪', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
}

const PROVIDER_ICON = {
  github:    '⌥ GitHub',
  gitlab:    '⌥ GitLab',
  bitbucket: '⌥ Bitbucket',
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function PublicViewer() {
  const { token } = useParams()
  const [doc, setDoc]       = useState(null)
  const [error, setError]   = useState(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    // Uses the shared api instance, not bare axios: this page hardcoded
    // `/api/...`, which only resolved because of the Vite dev proxy. In a
    // deployed build the client and API are on different hosts, so every
    // shared link 404'd.
    api.get(`/share/${token}`)
      .then(r => setDoc(r.data))
      .catch(e => setError(e.response?.data?.message ?? 'This link could not be found.'))
      .finally(() => setLoading(false))
  }, [token])

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-cosmic-gradient flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
        <p className="text-white/40 text-sm">Loading document…</p>
      </div>
    </div>
  )

  // ── Error / Expired ──────────────────────────────────────────────────────
  if (error) return (
    <div className="min-h-screen bg-cosmic-gradient flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <div className="text-5xl mb-6">🔒</div>
        <h1 className="font-display font-bold text-2xl text-white mb-3">Link unavailable</h1>
        <p className="text-white/50 mb-8">{error}</p>
        <Link to="/" className="btn-specze text-white inline-flex">
          Return to Specze
        </Link>
      </div>
    </div>
  )

  const meta = TYPE_META[doc.type] ?? TYPE_META.Document

  return (
    <div className="min-h-screen bg-cosmic-gradient">
      {/* Subtle glow */}
      <div className="fixed top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-purple-600/8 rounded-full blur-[120px] pointer-events-none" />

      {/* ── Navbar ──────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-40 border-b border-white/6 bg-cosmic-950/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between gap-4">
          <Link to="/" className="flex-shrink-0">
            <SpeczeLogo size={28} />
          </Link>

          <div className="flex items-center gap-3 min-w-0">
            {/* Type badge */}
            <span className={`hidden sm:flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${meta.bg} ${meta.color} ${meta.border}`}>
              {meta.icon} {doc.type}
            </span>

            {/* Title — truncated */}
            <h1 className="text-sm font-semibold text-white truncate max-w-xs md:max-w-md">
              {doc.title}
            </h1>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Version badge */}
            <span className="hidden sm:block text-[11px] font-mono text-white/35 bg-white/5 px-2 py-1 rounded-lg">
              v{doc.version}
            </span>

            {/* Copy link */}
            <button
              onClick={copyLink}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/6 border border-white/12 hover:bg-white/10 text-xs font-medium text-white/60 hover:text-white transition-all"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
              {copied ? 'Copied!' : 'Copy link'}
            </button>

            {/* Download */}
            {doc.download_url && (
              <a
                href={doc.download_url}
                target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 text-xs font-semibold text-purple-300 hover:text-purple-200 transition-all"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                </svg>
                Download
              </a>
            )}
          </div>
        </div>
      </nav>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <main className="max-w-5xl mx-auto px-6 py-10 relative">

        {/* Meta strip */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-8 text-xs text-white/35">
          {doc.repository?.full_name && (
            <span className="flex items-center gap-1.5">
              <span>{PROVIDER_ICON[doc.repository.provider] ?? '⌥ Repo'}</span>
              <code className="font-mono">{doc.repository.full_name}</code>
            </span>
          )}
          {doc.latest_version?.commit_sha && (
            <span className="flex items-center gap-1.5">
              <span>Commit</span>
              <code className="font-mono text-purple-400/60">{doc.latest_version.commit_sha}</code>
            </span>
          )}
          {doc.latest_version?.created_at && (
            <span>Generated {doc.latest_version.created_at}</span>
          )}
          {doc.latest_version?.is_diff && (
            <span className="text-blue-400/70 font-semibold">⚡ Diff update</span>
          )}
          <span className="flex items-center gap-1">👁 {doc.view_count} view{doc.view_count !== 1 ? 's' : ''}</span>
          {doc.share_expires_at && (
            <span className="text-amber-400/70">
              ⏱ Expires {new Date(doc.share_expires_at).toLocaleDateString()}
            </span>
          )}
        </div>

        {/* Diff summary — shown when this is a diff regeneration */}
        {doc.generation_mode === 'diff' && doc.diff_summary && (
          <div className="mb-8 p-5 rounded-2xl bg-blue-500/8 border border-blue-500/15">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-blue-400/70">
                ⚡ What Changed in this Update
              </span>
            </div>
            <pre className="text-xs text-white/60 font-mono whitespace-pre-wrap leading-relaxed">
              {doc.diff_summary}
            </pre>
          </div>
        )}

        {/* Document content area */}
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="px-8 py-6 border-b border-white/6 flex items-center justify-between">
            <h2 className="font-display font-semibold text-white text-lg">{doc.title}</h2>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${meta.bg} ${meta.color} ${meta.border}`}>
                {meta.icon} {doc.type}
              </span>
              <span className="text-xs font-mono text-white/25 bg-white/5 px-2 py-1 rounded-lg">v{doc.version}</span>
            </div>
          </div>

          {/* Main content placeholder — real content rendered from stored file */}
          <div className="px-8 py-10">
            {doc.status === 'complete' && doc.download_url ? (
              <div className="text-center space-y-6">
                <div className="text-5xl">{meta.icon}</div>
                <div>
                  <p className="text-white font-semibold mb-2">Document ready</p>
                  <p className="text-sm text-white/40 max-w-sm mx-auto">
                    This document was generated from the repository source code.
                    Download it to view the full content.
                  </p>
                </div>
                <a
                  href={doc.download_url}
                  target="_blank" rel="noreferrer"
                  className="btn-specze-lg text-white inline-flex mx-auto"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                  </svg>
                  Download Full Document
                </a>
              </div>
            ) : (
              <div className="text-center py-6 text-white/30 text-sm">
                Document generation in progress…
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 text-center">
          <p className="text-xs text-white/20">
            Generated by{' '}
            <Link to="/" className="text-purple-400/60 hover:text-purple-400 transition-colors">
              Specze
            </Link>
            {' '}— Turn your Git repositories into specifications, docs &amp; test plans.
          </p>
        </div>
      </main>
    </div>
  )
}
